import { useState, useEffect, useCallback, memo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import BrandLogo from '../components/BrandLogo'
import OnboardingTour from '../components/OnboardingTour'
import DeleteEventDialog from '../components/DeleteEventDialog'
import axios from 'axios'
import { Button } from '../components/ui/button'
import {
  Calendar,
  Plus,
  Gift,
  Copy,
  MoreHorizontal,
  ArrowRight,
  Loader2,
  LogOut,
  Settings,
  Share2,
  Trash2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { getApiUrl, resolveImageUrl } from '../lib/utils'
import { ownCover } from '../lib/defaultCovers'
import { cleanTemplateId, getTemplate } from '../lib/inviteTemplates'
import TemplateThumb from '../components/invite/TemplateThumb'

// The card's countdown re-renders it every second; its backdrop does not change.
const EventArt = memo(TemplateThumb)
import { countdownLabels, eventStartMs, useCountdown } from '../lib/countdown'
import { toast } from 'sonner'
import { Reveal } from '../components/celebration'

const API = getApiUrl()

const Dashboard = () => {
  const { user, logout } = useAuth()
  const { language, t } = useLanguage()
  const navigate = useNavigate()
  const [events, setEvents] = useState<any[]>([])
  const [eventFilter, setEventFilter] = useState('upcoming')
  const [loading, setLoading] = useState(true)
  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        withCredentials: true,
      })
      setEvents(response.data)
    } catch (error) {
      toast.error(t.failedToLoad)
    } finally {
      setLoading(false)
    }
  }, [t.failedToLoad])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  // After returning from the hosted card page (?payment=<id>), poll the
  // (callback-updated) status and report whether the package payment cleared.
  useEffect(() => {
    const paymentId = new URLSearchParams(window.location.search).get('payment')
    if (!paymentId) return
    let cancelled = false

    const finish = () => {
      if (!cancelled) window.history.replaceState({}, '', window.location.pathname)
    }
    const check = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/payments/${paymentId}`, {
          withCredentials: true,
        })
        if (cancelled) return
        if (data.status === 'paid') {
          toast.success(
            language === 'ka' ? 'გადახდა წარმატებულია!' : 'Payment successful!',
          )
          fetchEvents()
        } else if (data.status === 'pending' && attempt < 5) {
          setTimeout(() => check(attempt + 1), 2000)
          return
        } else if (data.status !== 'pending') {
          toast.error(
            language === 'ka' ? 'გადახდა ვერ დასრულდა' : 'Payment was not completed',
          )
        }
      } catch (e) {
        /* ignore */
      }
      finish()
    }
    check()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const copyInviteLink = (inviteLink) => {
    const fullLink = `${window.location.origin}/invite/${inviteLink}`
    navigator.clipboard.writeText(fullLink)
    toast.success(t.inviteLinkCopied)
  }

  // Deleting also moves the event's funds to the host's account balance, so it
  // goes through a dialog that fetches and states that figure first rather than
  // a bare window.confirm().
  const [pendingDelete, setPendingDelete] = useState(null)
  const requestDeleteEvent = (eventId) => {
    const target = events.find((e) => e.event_id === eventId)
    setPendingDelete({ eventId, title: target?.title || '' })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-celebrate flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-celebration-pink" />
      </div>
    )
  }

  const now = new Date()
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  // An event is "past" once its last day (end_date for multi-day events) is over.
  const isPastEvent = (event) => (event.end_date || event.date) < todayStr
  const filteredEvents = events.filter((event) =>
    eventFilter === 'upcoming'
      ? !isPastEvent(event)
      : eventFilter === 'past'
        ? isPastEvent(event)
        : true,
  )

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-celebration-ink/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <BrandLogo />

            <div className="flex min-w-0 items-center gap-1 sm:gap-3">
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />

              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button
                    variant="ghost"
                    className="h-9 w-9 px-0 sm:w-auto sm:px-4 text-gray-600 dark:text-gray-400"
                    data-testid="admin-link"
                  >
                    <Settings className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t.admin}</span>
                  </Button>
                </Link>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-gray-600 dark:text-gray-400 hover:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100dvh-4rem)] max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-[2.5rem] md:text-[3rem] font-bold font-display leading-none tracking-tight text-celebration-ink dark:text-[#f4f0ff]">
            {t.welcomeBackUser},{' '}
            <span className="text-gradient-brand">
              {user?.name?.split(' ')[0] || 'Host'}
            </span>
          </h1>
          <p className="text-celebration-ink-2 dark:text-[#c3b8dd] mt-3 text-lg">{t.manageEvents}</p>
        </div>

        {/* Onboarding walkthrough (Guidejar) */}
        <OnboardingTour />

        {/* Create Event Button */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-bold text-celebration-ink dark:text-[#f4f0ff] font-display">
              {t.yourEvents}
            </h2>
            <div
              className="relative grid w-fit grid-cols-3 rounded-full border border-celebration-ink/10 dark:border-white/10 bg-celebration-ink/[0.04] dark:bg-white/5 p-1"
              role="tablist"
              aria-label={language === 'ka' ? 'ივენთების ფილტრი' : 'Event filter'}
            >
              <span
                aria-hidden="true"
                className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/3)] rounded-full bg-white dark:bg-[#f4f0ff] shadow-[0_1px_2px_rgba(27,16,51,0.12),0_2px_8px_rgba(27,16,51,0.08)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
                style={{
                  transform: `translateX(${['upcoming', 'all', 'past'].indexOf(eventFilter) * 100}%)`,
                }}
              />
              {[
                { id: 'upcoming', en: 'Upcoming', ka: 'მომავალი' },
                { id: 'all', en: 'All', ka: 'ყველა' },
                { id: 'past', en: 'Past', ka: 'გასული' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={eventFilter === f.id}
                  onClick={() => setEventFilter(f.id)}
                  className={`relative z-10 rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-celebration-pink/40 ${
                    eventFilter === f.id
                      ? 'text-celebration-ink'
                      : 'text-celebration-ink-2 hover:text-celebration-ink dark:text-[#c3b8dd] dark:hover:text-white'
                  }`}
                  data-testid={`event-filter-${f.id}`}
                >
                  {language === 'ka' ? f.ka : f.en}
                </button>
              ))}
            </div>
          </div>
          <Link to="/events/create">
            <Button
              className="btn-gradient text-white rounded-full px-6"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.createEvent}
            </Button>
          </Link>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <div className="tile relative overflow-hidden p-12 text-center animate-fade-in">
            <div
              aria-hidden="true"
              className="blob blob-pink blob-drift"
              style={{ width: 240, height: 240, top: -110, left: -90, opacity: 0.3 }}
            />
            <div
              aria-hidden="true"
              className="blob blob-violet blob-drift-slow"
              style={{ width: 280, height: 280, bottom: -130, right: -110, opacity: 0.25 }}
            />
            <span
              aria-hidden="true"
              className="absolute left-[16%] top-10 text-2xl animate-celebrate-float"
            >
              🎈
            </span>
            <span
              aria-hidden="true"
              className="absolute right-[15%] top-16 text-2xl animate-celebrate-float"
              style={{ animationDelay: '1.4s' }}
            >
              ✨
            </span>
            <div className="relative">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'linear-gradient(135deg, #e9defb, #d7ecff)' }}
              >
                <Calendar className="w-8 h-8 text-celebration-grape" />
              </div>
              <h3 className="text-lg font-semibold text-celebration-ink dark:text-[#f4f0ff] font-display mb-2">
                {t.noEventsYet}
              </h3>
              <p className="text-celebration-ink-2 dark:text-[#c3b8dd] mb-6">{t.createFirstEventDesc}</p>
              <Link to="/events/create">
                <Button
                  className="btn-gradient text-white rounded-full px-8"
                  data-testid="create-first-event-btn"
                >
                  {t.createFirstEvent}
                </Button>
              </Link>
            </div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="tile p-12 text-center animate-fade-in text-celebration-ink-2 dark:text-[#c3b8dd]">
            {eventFilter === 'upcoming'
              ? language === 'ka'
                ? 'მომავალი ივენთები არ არის'
                : 'No upcoming events'
              : language === 'ka'
                ? 'გასული ივენთები არ არის'
                : 'No past events'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event, index) => (
              <Reveal key={event.event_id} delay={(index % 3) * 0.12} pop>
                <EventCard
                  event={event}
                  onCopyLink={copyInviteLink}
                  onDelete={requestDeleteEvent}
                  onOpenDetails={() => navigate(`/events/${event.event_id}`)}
                  t={t}
                  language={language}
                  faded={isPastEvent(event)}
                />
              </Reveal>
            ))}
          </div>
        )}
      </main>
      <AppFooter />

      <DeleteEventDialog
        eventId={pendingDelete?.eventId || null}
        eventTitle={pendingDelete?.title}
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        onDeleted={() => {
          setPendingDelete(null)
          fetchEvents()
        }}
      />
    </div>
  )
}

const EventCard = ({
  event,
  onCopyLink,
  onDelete,
  onOpenDetails,
  t,
  language,
  style = undefined,
  faded = false,
}) => {
  const [rsvpData, setRsvpData] = useState(null)
  const [wishlistData, setWishlistData] = useState(null)

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const [rsvpRes, wishlistRes] = await Promise.all([
          axios.get(`${API}/events/${event.event_id}/rsvp-summary`, {
            withCredentials: true,
          }),
          axios.get(`${API}/events/${event.event_id}/wishlist`, {
            withCredentials: true,
          }),
        ])
        setRsvpData(rsvpRes.data)
        setWishlistData(wishlistRes.data)
      } catch (error) {
        console.error('Failed to fetch event data')
      }
    }
    fetchEventData()
  }, [event.event_id])

  const totalWishlistTarget =
    wishlistData?.reduce((acc, item) => acc + item.target_amount, 0) || 0
  const totalWishlistCollected =
    wishlistData?.reduce((acc, item) => acc + item.collected_amount, 0) || 0
  const wishlistProgress =
    totalWishlistTarget > 0
      ? (totalWishlistCollected / totalWishlistTarget) * 100
      : 0

  // Live countdown to the start, shown across the card's open middle. Same
  // clock as the guest invitation (lib/countdown), so a host never sees a
  // different number from their guests.
  const countdown = useCountdown(eventStartMs(event.date, event.time))
  const countLabels = countdownLabels(language)

  // The card's backdrop is the invitation's own look: a painted design's
  // artwork always (it carries no photo), a classic design's picture unless
  // the host uploaded a photo of their own, and the host's photo on Auto.
  const cardTemplate = getTemplate(cleanTemplateId(event.invite_template))
  const photo = ownCover(event.cover_image)
  const showArt = cardTemplate.id !== 'auto' && (!!cardTemplate.painted || !photo)

  // Theme colors
  const themeColors = {
    classic: 'from-slate-800 to-slate-900',
    modern: 'from-gray-900 to-black',
    romantic: 'from-rose-400 to-pink-500',
    dark: 'from-zinc-900 to-black',
    kids: 'from-yellow-400 via-orange-400 to-pink-500',
  }

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const inviteUrl = `${window.location.origin}/invite/${event.invite_link}`
    const message = encodeURIComponent(
      `You're invited to ${event.title}! RSVP: ${inviteUrl}`,
    )
    window.open(`https://wa.me/?text=${message}`, '_blank')
  }

  const handleCardKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOpenDetails()
    }
  }

  return (
    <div
      className={`group relative rounded-bubble border border-white/50 dark:border-white/10 shadow-celebrate-sm overflow-hidden card-hover cursor-pointer ${
        faded
          ? '!opacity-60 hover:!opacity-100 transition-opacity duration-300 border-dashed shadow-none'
          : ''
      }`}
      style={style}
      data-testid={`event-card-${event.event_id}`}
      role="button"
      tabIndex={0}
      onClick={onOpenDetails}
      onKeyDown={handleCardKeyDown}
    >
      {/* Full-card background: the template's picture, the host's photo or
          the theme gradient; zooms gently on hover */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-gradient-to-br ${themeColors[event.theme] || themeColors.classic} transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100`}
        data-testid={`event-card-art-${event.event_id}`}
      >
        {showArt ? (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
            <EventArt
              template={cardTemplate}
              lang={language}
              accent={event.accent_color}
              background={event.background_color}
              bare
              className="w-full !rounded-none !shadow-none"
            />
          </div>
        ) : photo ? (
          <img
            src={resolveImageUrl(photo)}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      {/* Cinematic scrim: heavy at the bottom where the content lives */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/15"
      />

      {/* Content overlay */}
      <div className="relative flex min-h-[400px] flex-col p-4">
        {/* justify-end, not justify-between: the type/language badges below are
            absolutely positioned at top-left and were landing on top of this
            menu button, which sits in normal flow. Keeping the button hard
            right is what the badges' pr-12 already reserves space for. */}
        <div className="flex items-start justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* The card background is a photo or the template's picture, so
                  a 15%-white pill vanished over anything light. A dark scrim
                  behind the icon keeps the menu findable on every image. */}
              <Button
                variant="ghost"
                size="icon"
                aria-label={language === 'ka' ? 'ღონისძიების მენიუ' : 'Event menu'}
                className="cursor-pointer h-8 w-8 shrink-0 rounded-full bg-black/45 text-white shadow-[0_1px_6px_rgba(0,0,0,0.35)] ring-1 ring-white/30 backdrop-blur-md transition-colors hover:bg-black/65 hover:text-white"
                onClick={(e) => e.stopPropagation()}
                data-testid={`event-card-menu-${event.event_id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem onClick={() => onCopyLink(event.invite_link)}>
                <Copy className="w-4 h-4 mr-2" />
                {t.copyLink}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareViaWhatsApp}>
                <Share2 className="w-4 h-4 mr-2" />
                WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(event.event_id)}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t.delete}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="absolute top-4 left-4 flex flex-wrap gap-2 pr-12">
          {faded && (
            <span className="bg-black/45 backdrop-blur-md ring-1 ring-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
              {language === 'ka' ? 'გასული' : 'Past'}
            </span>
          )}
          <span className="bg-white/15 backdrop-blur-md ring-1 ring-white/25 text-white text-xs font-medium px-3 py-1 rounded-full">
            {event.event_type}
          </span>
          {(event.language === 'ka' || event.language === 'both') && (
            <span className="bg-white/15 backdrop-blur-md ring-1 ring-white/25 text-white text-xs font-medium px-2 py-1 rounded-full">
              {event.language === 'both' ? 'KA · EN' : 'KA'}
            </span>
          )}
        </div>

        {/* Photo breathes; content sits on the scrim. The countdown floats in
            this open middle band — centred, so it reads as part of the photo
            rather than another row of card chrome. */}
        <div className="flex flex-1 items-center justify-center py-4">
          {!countdown.done && (
            <div
              className="flex w-4/5 items-stretch gap-2"
              aria-label={`${countdown.d}${countLabels.d} ${countdown.h}:${countdown.m}:${countdown.s}`}
              data-testid={`event-countdown-${event.event_id}`}
            >
              {(['d', 'h', 'm', 's'] as const).map((k) => (
                <div
                  key={k}
                  className="flex flex-1 flex-col items-center justify-center rounded-2xl bg-black/40 px-1 py-2.5 ring-1 ring-white/15 backdrop-blur-md"
                >
                  <span className="font-display text-2xl font-bold leading-none text-white tabular-nums">
                    {countdown[k]}
                  </span>
                  <span className="mt-1 text-[10px] font-medium uppercase leading-none tracking-wide text-white/60">
                    {countLabels[k]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <h3 className="text-xl font-semibold font-display tracking-tight text-white mb-0.5 truncate">
          {event.title}
        </h3>
        <p className="text-[13px] text-white/70 mb-3">
          {new Date(event.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}{' '}
          · {event.time}
        </p>

        {/* RSVP Stats — segmented bar + colored counts */}
        {rsvpData && (
          <div className="mb-3">
            {(() => {
              const totalRsvp =
                (rsvpData.attending || 0) +
                (rsvpData.pending || 0) +
                (rsvpData.declined || 0)
              return (
                <div className="flex h-1.5 w-full gap-[3px] overflow-hidden">
                  {totalRsvp === 0 ? (
                    <div className="h-full w-full rounded-full bg-white/25" />
                  ) : (
                    <>
                      {rsvpData.attending > 0 && (
                        <div
                          className="h-full rounded-full bg-green-400"
                          style={{
                            width: `${(rsvpData.attending / totalRsvp) * 100}%`,
                          }}
                        />
                      )}
                      {rsvpData.pending > 0 && (
                        <div
                          className="h-full rounded-full bg-amber-300"
                          style={{
                            width: `${(rsvpData.pending / totalRsvp) * 100}%`,
                          }}
                        />
                      )}
                      {rsvpData.declined > 0 && (
                        <div
                          className="h-full rounded-full bg-red-400"
                          style={{
                            width: `${(rsvpData.declined / totalRsvp) * 100}%`,
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              )
            })()}
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-white/80 ring-1 ring-white/15 backdrop-blur-sm">
                <span className="font-semibold text-green-300">
                  {rsvpData.attending}
                </span>{' '}
                {t.attending.toLowerCase()}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-white/80 ring-1 ring-white/15 backdrop-blur-sm">
                <span className="font-semibold text-amber-300">
                  {rsvpData.pending}
                </span>{' '}
                {t.pending.toLowerCase()}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-white/80 ring-1 ring-white/15 backdrop-blur-sm">
                <span className="font-semibold text-red-300">
                  {rsvpData.declined}
                </span>{' '}
                {t.declined.toLowerCase()}
              </span>
            </div>
          </div>
        )}

        {/* Wishlist Progress — shown for every event, even with no items yet (0%) */}
        {wishlistData && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-white/75 flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" />
                {t.wishlist}
              </span>
              <span className="font-semibold text-white/90 tabular-nums">
                {Math.round(wishlistProgress)}%
              </span>
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full rounded-full bg-white/90"
                style={{ width: `${Math.min(wishlistProgress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Actions: glass share pills + open button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onCopyLink(event.invite_link)
            }}
            className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-white/15 text-white text-xs font-medium ring-1 ring-white/25 backdrop-blur-md transition-colors hover:bg-white/30"
            data-testid={`quick-copy-${event.event_id}`}
          >
            <Copy className="w-3.5 h-3.5" />
            {t.copyLink}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              shareViaWhatsApp()
            }}
            className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-white/15 text-white text-xs font-medium ring-1 ring-white/25 backdrop-blur-md transition-colors hover:bg-white/30"
            data-testid={`quick-whatsapp-${event.event_id}`}
          >
            <Share2 className="w-3.5 h-3.5" />
            WhatsApp
          </button>
          <button
            type="button"
            aria-label={t.viewDetails}
            onClick={(e) => {
              e.stopPropagation()
              onOpenDetails()
            }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-celebration-ink shadow-sm transition-transform duration-200 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100"
            data-testid={`view-event-${event.event_id}`}
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
